/**
 * Active Sessions — Account Security.
 *
 * Lists every currently valid session for the signed-in user (server
 * registry, safe metadata only) with per-session sign-out and
 * "Sign out all other devices". Terminating any OTHER session requires
 * recent MFA — a 403 step-up auto-prompts a fresh OTP and retries.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Desktop, DeviceMobile, ShieldCheck, SignOut, Monitor,
} from '@phosphor-icons/react';
import MfaStepUpDialog from '../auth/MfaStepUpDialog';

const deviceIcon = (os) => {
  if (os === 'iOS' || os === 'Android') return <DeviceMobile size={22} className="text-slate-500" />;
  if (os === 'Unknown OS') return <Monitor size={22} className="text-slate-500" />;
  return <Desktop size={22} className="text-slate-500" />;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export const ActiveSessionsCard = () => {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // session obj or 'others'
  const [busy, setBusy] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const pendingRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/auth/sessions');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runWithStepUp = useCallback(async (fn) => {
    setBusy(true);
    try {
      await fn();
      setConfirmTarget(null);
      load();
    } catch (err) {
      const structured = err.response?.data?.detail_object;
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 &&
          (structured?.error === 'mfa_step_up_required' || detail?.error === 'mfa_step_up_required')) {
        pendingRef.current = fn;
        setStepUpOpen(true);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Action failed');
      }
    } finally {
      setBusy(false);
    }
  }, [load]);

  const signOutSession = useCallback((session) => runWithStepUp(async () => {
    await api.delete(`/auth/sessions/${session.id}`);
    if (session.is_current) {
      toast.success('Signed out');
      await logout();
      window.location.href = '/login';
      return;
    }
    toast.success('Device signed out');
  }), [runWithStepUp, logout]);

  const signOutOthers = useCallback(() => runWithStepUp(async () => {
    const r = await api.post('/auth/sessions/revoke-others');
    toast.success(`Signed out ${r.data.revoked_count} other device${r.data.revoked_count === 1 ? '' : 's'}`);
  }), [runWithStepUp]);

  if (!data) return null;
  const sessions = data.sessions || [];
  const others = sessions.filter((s) => !s.is_current);

  return (
    <Card data-testid="active-sessions-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Monitor size={18} className="text-indigo-700" /> Active sessions
            </CardTitle>
            <CardDescription>
              Everywhere your account is currently signed in. Sessions end automatically
              after {Math.round((data.idle_timeout_seconds || 900) / 60)} minutes of inactivity
              or {data.absolute_lifetime_hours} hours total.
            </CardDescription>
          </div>
          {others.length > 0 && (
            <Button
              variant="outline" size="sm"
              onClick={() => setConfirmTarget('others')}
              data-testid="signout-all-others-btn"
            >
              <SignOut size={14} className="mr-1" /> Sign out all other devices
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {sessions.map((s) => (
          <div
            key={s.id}
            className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${s.is_current ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200'}`}
            data-testid={`session-row-${s.id_masked.replace('…', '')}`}
          >
            {deviceIcon(s.os_summary)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-900">{s.device_summary}</span>
                {s.is_current && (
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200" data-testid="current-session-badge">
                    This device
                  </Badge>
                )}
                {s.mfa_verified && (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    <ShieldCheck size={12} className="mr-1" /> MFA
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Signed in {fmt(s.issued_at)} · Last active {fmt(s.last_activity_at)}
              </p>
              <p className="text-xs text-slate-400">
                Network {s.ip_masked} · Session {s.id_masked} · Expires {fmt(s.expires_at)}
              </p>
            </div>
            <Button
              variant={s.is_current ? 'destructive' : 'outline'} size="sm"
              onClick={() => setConfirmTarget(s)}
              data-testid={`session-signout-btn-${s.id_masked.replace('…', '')}`}
            >
              {s.is_current ? 'Sign out this device' : 'Sign out'}
            </Button>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-sm text-slate-500">No active sessions.</p>}
      </CardContent>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="session-signout-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmTarget === 'others'
                ? `Sign out ${others.length} other device${others.length === 1 ? '' : 's'}?`
                : confirmTarget?.is_current
                  ? 'Sign out this device?'
                  : `Sign out ${confirmTarget?.device_summary}?`}
            </DialogTitle>
            <DialogDescription>
              {confirmTarget === 'others'
                ? 'Every session except this one will be terminated immediately. A fresh authenticator code may be required.'
                : confirmTarget?.is_current
                  ? 'You will be returned to the sign-in screen.'
                  : 'That device will be signed out immediately. A fresh authenticator code may be required.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTarget(null)}>Cancel</Button>
            <Button
              variant="destructive" disabled={busy}
              onClick={() => (confirmTarget === 'others' ? signOutOthers() : signOutSession(confirmTarget))}
              data-testid="session-signout-confirm-btn"
            >
              {busy ? 'Signing out…' : 'Sign out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MfaStepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        onVerified={() => { const fn = pendingRef.current; pendingRef.current = null; if (fn) runWithStepUp(fn); }}
      />
    </Card>
  );
};

export default ActiveSessionsCard;
